import { autocompletion, startCompletion } from "@codemirror/autocomplete";
import { oneDark } from "@uiw/react-codemirror";
import { keymap } from "@codemirror/view";
import { toast } from "react-toastify";
import { fetchCompletionsFromBackend } from "ServerAPI/serverAPI";
import { EditorView } from "@codemirror/view";

// Debounce utility to limit API calls
const debounce = (func, wait) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    return new Promise((resolve) => {
      timeout = setTimeout(() => resolve(func(...args)), wait);
    });
  };
};

// Debounced fetch function
const debouncedFetchCompletions = debounce(fetchCompletionsFromBackend, 500);

// Helper function to truncate text smartly
const truncateText = (text, maxLength = 80) => {
  if (text.length <= maxLength) return text;
  
  // Try to break at word boundary
  const truncated = text.substring(0, maxLength);
  const lastSpace = truncated.lastIndexOf(' ');
  
  if (lastSpace > maxLength * 0.6) {
    return truncated.substring(0, lastSpace) + '...';
  }
  
  return truncated + '...';
};

// Helper function to convert completion to insertable code
const convertToCode = (completionText, language = 'python') => {
  // Comment prefix based on language
  const commentPrefix = language === 'python' ? '# ' : '// ';
  
  // If it's a description/hint, convert to comment
  if (completionText.includes('Declare') || 
      completionText.includes('print') || 
      completionText.includes('function') ||
      completionText.length > 50) {
    
    // Split into multiple lines if too long
    const maxLineLength = 60;
    const words = completionText.split(' ');
    const lines = [];
    let currentLine = '';
    
    words.forEach(word => {
      if ((currentLine + word).length > maxLineLength && currentLine.length > 0) {
        lines.push(commentPrefix + currentLine.trim());
        currentLine = word + ' ';
      } else {
        currentLine += word + ' ';
      }
    });
    
    if (currentLine.trim()) {
      lines.push(commentPrefix + currentLine.trim());
    }
    
    return lines.join('\n');
  }
  
  // If it looks like code, return as is
  return completionText;
};

// Create tooltip styling extension
const tooltipStyling = EditorView.theme({
  ".cm-tooltip": {
    maxWidth: "500px !important",
    maxHeight: "200px !important",
    overflow: "hidden !important",
    border: "1px solid #444 !important",
    borderRadius: "4px !important",
    boxShadow: "0 2px 8px rgba(0,0,0,0.3) !important",
    backgroundColor: "#1e1e1e !important",
    zIndex: "1000 !important"
  },
  ".cm-tooltip-autocomplete": {
    maxWidth: "500px !important",
    maxHeight: "300px !important",
    overflowY: "auto !important",
    backgroundColor: "#1e1e1e !important",
    border: "1px solid #444 !important",
    borderRadius: "4px !important",
    boxShadow: "0 4px 12px rgba(0,0,0,0.4) !important"
  },
  ".cm-tooltip-autocomplete > ul": {
    maxWidth: "100% !important"
  },
  ".cm-tooltip-autocomplete ul li": {
    maxWidth: "480px !important",
    whiteSpace: "nowrap !important",
    overflow: "hidden !important",
    textOverflow: "ellipsis !important",
    padding: "6px 10px !important",
    cursor: "pointer !important",
    borderBottom: "1px solid #333 !important",
    fontFamily: "'Monaco', 'Menlo', 'Ubuntu Mono', monospace !important",
    fontSize: "12px !important"
  },
  ".cm-tooltip-autocomplete ul li:hover": {
    backgroundColor: "#2d2d2d !important"
  },
  ".cm-tooltip-autocomplete ul li[aria-selected='true']": {
    backgroundColor: "#094771 !important",
    color: "white !important"
  },
  ".cm-completionDetail": {
    fontStyle: "italic !important",
    color: "#888 !important",
    fontSize: "10px !important",
    marginLeft: "8px !important"
  },
  ".cm-tooltip.cm-tooltip-info": {
    maxWidth: "400px !important",
    maxHeight: "200px !important",
    overflowWrap: "break-word !important",
    whiteSpace: "pre-wrap !important",
    padding: "8px !important",
    backgroundColor: "#252525 !important",
    border: "1px solid #444 !important",
    borderRadius: "4px !important",
    fontFamily: "'Monaco', 'Menlo', 'Ubuntu Mono', monospace !important",
    fontSize: "11px !important",
    lineHeight: "1.4 !important"
  }
});

// Completion source for CodeMirror
export const myCompletionSource = (problemStatement, language, testCases, sessionId, setSessionId) => {
  return async (context) => {
    // console.log("myCompletionSource triggered at position:", context.pos);
    const { state } = context;
    const line = state.doc.lineAt(state.selection.main.head);
    const codeBeforeCursor = state.sliceDoc(line.from, state.selection.main.head);
    // console.log("Code before cursor:", codeBeforeCursor);

    try {
      const { completion: completionText, session_id: returnedSessionId } = await debouncedFetchCompletions(
        codeBeforeCursor,
        language,
        problemStatement,
        testCases,
        sessionId
      );

      if (returnedSessionId && returnedSessionId !== sessionId) {
        // console.log("Updating sessionId:", returnedSessionId);
        setSessionId(returnedSessionId);
      }

      if (!completionText) {
        // console.log("No completion text, trying fallback");
        const { completion: fallbackCompletion, session_id: fallbackSessionId } = await debouncedFetchCompletions(
          "",
          language,
          problemStatement,
          testCases,
          sessionId
        );
        if (fallbackSessionId && fallbackSessionId !== sessionId) {
          setSessionId(fallbackSessionId);
        }
        if (!fallbackCompletion) {
          // console.log("No fallback completion available");
          toast.warn("No suggestion available");
          return null;
        }
        
        const processedFallback = convertToCode(fallbackCompletion.trim(), language);
        // console.log("Fallback completion:", processedFallback);
        
        return {
          from: context.pos,
          options: [{
            label: truncateText(fallbackCompletion),
            apply: processedFallback,
            type: "text",
            detail: "💡 ",
            info: fallbackCompletion,
          }],
        };
      }

      const cleanedCompletion = completionText.replace(/^\w+\n/, '').trim();
      // console.log("Cleaned completion:", cleanedCompletion);
      
      const processedCompletion = convertToCode(cleanedCompletion, language);
      const displayLabel = truncateText(cleanedCompletion);
      
      // console.log("Processed completion:", processedCompletion);
      
      return {
        from: context.pos,
        options: [{
          label: displayLabel,
          apply: processedCompletion,
          type: "text",
          detail: "💡",
          info: cleanedCompletion,
        }],
      };
    } catch (error) {
      // console.error("Completion error:", error.message);
      toast.error(`Failed to load suggestion: ${error.message}`);
      return null;
    }
  };
};

export const createAssistanceExtensions = (problemStatement, language, testCases, sessionId, setSessionId) => {
  return [
    tooltipStyling, // Add the styling extension
    autocompletion({
      override: [myCompletionSource(problemStatement, language, testCases, sessionId, setSessionId)],
      defaultKeymap: true,
      activateOnTyping: false,
      maxRenderedOptions: 3,
      closeOnBlur: true,
    }),
    keymap.of([
      { key: "Ctrl-Space", run: startCompletion },
    ]),
    oneDark,
  ];
};